import { type AccountInfo } from '@azure/msal-browser';
import { decodeJWT, validateOfflineToken } from './authTokenValidation';
import { getOfflineAuthService } from './offlineAuthService';

export { decodeJWT, validateOfflineToken } from './authTokenValidation';

export interface OfflineAuthMetadata {
  tokenStoredAt: number;
  tokenExpiry: number;
  userPreferences?: Record<string, any>;
  lastOnlineTimestamp: number;
}

export const setAccount = (
  account: AccountInfo,
  accessToken: string,
  offlineCapable = true
) => {
  localStorage.setItem('auth.account', JSON.stringify(account));
  localStorage.setItem('auth.accessToken', accessToken);
  localStorage.setItem('auth.isAuthenticated', 'true');

  // Store additional metadata for offline support
  if (offlineCapable) {
    const tokenInfo = decodeJWT(accessToken);
    const metadata: OfflineAuthMetadata = {
      tokenStoredAt: Date.now(),
      tokenExpiry: tokenInfo?.exp
        ? tokenInfo.exp * 1000
        : Date.now() + 24 * 60 * 60 * 1000, // Default 24h if no exp
      lastOnlineTimestamp: Date.now(),
      userPreferences: {},
    };
    localStorage.setItem('auth.offlineMetadata', JSON.stringify(metadata));
  }
};

export const getAccount = (): AccountInfo | null => {
  try {
    const account = localStorage.getItem('auth.account');
    return account ? JSON.parse(account) : null;
  } catch {
    return null;
  }
};

/**
 * Synchronous read of the stored access token. Prefer this over poking
 * localStorage directly. When you care whether the cached token is still
 * usable offline, pass the returned value to `validateOfflineToken`.
 */
export const getAccessToken = (): string =>
  localStorage.getItem('auth.accessToken') ||
  localStorage.getItem('token') ||
  '';

/**
 * Decodes the currently stored access token and returns its payload, or
 * `null` if no token is stored / decoding fails. Use this instead of manually
 * reading localStorage and calling decodeJWT in components.
 *
 * NOTE: Only use this for session/user-level data (e.g. tenantId, codeConvention).
 * Tenant configuration (feature flags, otherInfo) must be fetched from
 * `GET /a/tenant` instead of read from the JWT.
 */
export function getDecodedToken(): Record<string, any> | null {
  const token = getAccessToken();
  if (!token) return null;
  return decodeJWT(token) ?? null;
}

/**
 * Returns true when the `codeConvention` claim defines a convention for the
 * given entity (e.g., 'asset', 'work', 'order'). Convention strings look like
 * 'asset:<format>;work:<format>'.
 */
export function hasCodeConvention(entity: 'asset' | 'work' | 'order'): boolean {
  const cc = getDecodedToken()?.codeConvention;
  return typeof cc === 'string' && cc.includes(`${entity}:`);
}

/**
 * Returns the user's roles from the backend access token, normalized to a
 * string[]. Tolerates the claim being missing, a single string, a
 * comma/space-separated string, or an array of strings.
 */
export function getRolesFromToken(): string[] {
  const raw = getDecodedToken()?.roles;
  if (Array.isArray(raw)) {
    return raw.filter(
      (r): r is string => typeof r === 'string' && r.length > 0
    );
  }
  if (typeof raw === 'string' && raw.length > 0) {
    return raw
      .split(/[,\s]+/)
      .map((r) => r.trim())
      .filter(Boolean);
  }
  return [];
}

export function getActiveRoleFromToken(): string | null {
  const payload = getDecodedToken();
  const named = payload?.defaultRole;
  if (typeof named === 'string' && named.length > 0) return named;
  return getRolesFromToken()[0] ?? null;
}

/**
 * True when the current session's active (or any held) role is the
 * contractor role. Web-only (relies on localStorage-backed token storage).
 */
export function isContractorRole(): boolean {
  const active = getActiveRoleFromToken();
  const roles = active ? [active] : getRolesFromToken();
  return roles.includes('FMSContractor.Access');
}

const tokenClaim = (
  payload: Record<string, any> | null,
  key: string
): string => {
  const value = payload?.[key];
  return typeof value === 'string' ? value.trim() : '';
};

export function getWelcomeNameFromToken(): string | null {
  const payload = getDecodedToken();
  const name =
    tokenClaim(payload, 'displayname') ||
    tokenClaim(payload, 'firstname') ||
    tokenClaim(payload, 'lastname') ||
    tokenClaim(payload, 'name');
  return name || null;
}

export const isAuthenticated = (): boolean => {
  const authFlag = localStorage.getItem('auth.isAuthenticated');
  const account = getAccount();
  const token = getAccessToken();

  return authFlag === 'true' && !!account && !!token;
};

export const canWorkOffline = async (): Promise<boolean> => {
  if (!isAuthenticated()) return false;

  const token = getAccessToken();
  if (!token) return false;

  return await validateOfflineToken(token);
};

export const getOfflineMetadata = (): OfflineAuthMetadata | null => {
  try {
    const metadata = localStorage.getItem('auth.offlineMetadata');
    return metadata ? JSON.parse(metadata) : null;
  } catch {
    return null;
  }
};

export const updateOfflineMetadata = (
  updates: Partial<OfflineAuthMetadata>
) => {
  const current = getOfflineMetadata() || {
    tokenStoredAt: Date.now(),
    tokenExpiry: Date.now() + 24 * 60 * 60 * 1000,
    lastOnlineTimestamp: Date.now(),
  };

  const updated = { ...current, ...updates };
  localStorage.setItem('auth.offlineMetadata', JSON.stringify(updated));
};

export const logout = () => {
  localStorage.removeItem('auth.account');
  localStorage.removeItem('auth.accessToken');
  localStorage.removeItem('auth.offlineMetadata');
  localStorage.removeItem('auth.isOffline');
  localStorage.removeItem('auth.offlineTimestamp');
  localStorage.setItem('auth.isAuthenticated', 'false');

  const offlineSvc = getOfflineAuthService();
  offlineSvc?.checkAuthState();
};

export const msalLogoutRedirect = async (
  instance: {
    initialize: () => Promise<void>;
    getActiveAccount: () => AccountInfo | null;
    getAllAccounts: () => AccountInfo[];
    logoutRedirect: (request?: {
      account?: AccountInfo;
      authority?: string;
      postLogoutRedirectUri?: string;
    }) => Promise<void>;
    clearCache: (options?: { account?: AccountInfo }) => Promise<void>;
  },
  options?: {
    authority?: string;
    postLogoutRedirectUri?: string;
  }
): Promise<void> => {
  await instance.initialize();
  const account =
    instance.getActiveAccount() ?? instance.getAllAccounts()[0] ?? undefined;
  const authority = options?.authority;
  const postLogoutRedirectUri = options?.postLogoutRedirectUri ?? '/';

  logout();

  try {
    if (authority) {
      await instance.logoutRedirect({
        ...(account ? { account } : {}),
        authority,
        postLogoutRedirectUri,
      });
      return;
    }
  } catch {
    /* */
  }

  try {
    await instance.clearCache();
  } catch {
    /* */
  }
  window.location.assign(postLogoutRedirectUri);
};
